import Modal from 'antd/lib/modal';
import * as React from 'react';
import { FC } from 'react';
import Form from 'antd/lib/form';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { OrgSettingsUpdatable } from '@/actions/updatable';
import Select from 'antd/lib/select';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { updateTimezonePreferenceSuccess } from '@/actions/user';
import { useDispatch } from 'react-redux';
import { globalMessage } from '@/utils/globalMessage';
import Text from 'antd/lib/typography/Text';
import dayjs from 'dayjs';

type EditTimezoneSettingsModalProps = {
    open: boolean;
    onCancel: () => void;
    userId: string;
    orgId: string;
    timezonePreference?: TimezonePreferenceEnum;
    orgTimezone?: string;
};

export enum TimezonePreferenceEnum {
    Local = 'local',
    ScheduleSettings = 'scheduleSettings',
    OrgWide = 'orgWide',
}
const EditTimezoneSettingsModal: FC<EditTimezoneSettingsModalProps> = ({
    userId,
    open,
    onCancel,
    orgId,
    timezonePreference,
    orgTimezone,
}: EditTimezoneSettingsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const editTimezonePreference = (value: TimezonePreferenceEnum) => {
        const request = { userId, orgId, timezonePreference: value };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateTimezonePreferenceForOrgUser, request));
    };

    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateTimezonePreferenceForOrgUser);
    const isUpdated = useUpdated(OrgSettingsUpdatable.UpdateTimezonePreferenceForOrgUser);
    const updateError = useError(OrgSettingsUpdatable.UpdateTimezonePreferenceForOrgUser);

    React.useEffect(() => {
        if (isUpdated) {
            dispatch(updateTimezonePreferenceSuccess(form.getFieldValue('timezonePreference')));
            globalMessage.success(`Timezone preference updated successfully`);
            onCancel();
        }
    }, [isUpdated]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    return (
        <Modal
            title="Edit timezone settings"
            okButtonProps={{ loading: isLoading }}
            onOk={() => form.submit()}
            okText="Save"
            open={open}
            onCancel={onCancel}
            width={550}
            afterClose={() => {
                form.resetFields();
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Text>
                    Runway will adjust all dates displayed within the platform based on your timezone preference.
                </Text>
                <Form
                    onFinish={editTimezonePreference}
                    initialValues={{
                        timezonePreference: timezonePreference ?? TimezonePreferenceEnum.ScheduleSettings,
                    }}
                    style={{ marginTop: 24, width: '100%' }}
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="timezonePreference"
                        label="Timezone preference"
                        style={{ width: '100%' }}
                        labelAlign={'left'}
                    >
                        <Select placeholder={'Select'} autoFocus>
                            {[
                                <Select.Option key={TimezonePreferenceEnum.Local} value={TimezonePreferenceEnum.Local}>
                                    Show times in my local timezone
                                </Select.Option>,
                                <Select.Option
                                    key={TimezonePreferenceEnum.ScheduleSettings}
                                    value={TimezonePreferenceEnum.ScheduleSettings}
                                >
                                    {"Show times in the timezone selected in the app's Schedule settings"}
                                </Select.Option>,
                                <Select.Option
                                    key={TimezonePreferenceEnum.OrgWide}
                                    value={TimezonePreferenceEnum.OrgWide}
                                >
                                    {`Show times in my organization's default timezone (${orgTimezone ? dayjs().tz(orgTimezone).format('z') : 'Not set'})`}
                                </Select.Option>,
                            ]}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default EditTimezoneSettingsModal;
